import type { MotionPreset } from '@loop/shared/theme';

/**
 * Motion 프리셋 구현. docs/DESIGN.md §3.2 duration/easing 그대로.
 *
 * enter / exit는 Web Animations API 용 keyframes + options를 반환합니다.
 * Framer Motion이나 GSAP에 의존하지 않고 표준 API만 사용 — OBS 브라우저 소스의
 * 크롬 버전에 종속되지 않고 Chrome 105+ 전부 커버됩니다.
 *
 * spring은 WAAPI에 네이티브로 없으므로 cubic-bezier로 근사합니다.
 */

export interface MotionSpec {
  readonly keyframes: Keyframe[];
  readonly options: KeyframeAnimationOptions;
}

export interface MotionPresetSpec {
  readonly enter: MotionSpec;
  readonly exit: MotionSpec;
}

/* spring 근사용 cubic-bezier. Framer Motion의 default spring과 유사한 곡선. */
const SPRING_STIFF = 'cubic-bezier(0.175, 0.885, 0.32, 1.275)';
const SPRING_SOFT = 'cubic-bezier(0.22, 1, 0.36, 1)';

const snap: MotionPresetSpec = {
  enter: {
    keyframes: [
      { opacity: 0 },
      { opacity: 1 },
    ],
    options: { duration: 80, easing: 'cubic-bezier(0,0,0,1)', fill: 'forwards' },
  },
  exit: {
    keyframes: [{ opacity: 1 }, { opacity: 0 }],
    options: { duration: 120, easing: 'linear', fill: 'forwards' },
  },
};

const float: MotionPresetSpec = {
  enter: {
    keyframes: [
      { opacity: 0, transform: 'translateY(12px)' },
      { opacity: 1, transform: 'translateY(0)' },
    ],
    options: { duration: 300, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' },
  },
  exit: {
    keyframes: [
      { opacity: 1, transform: 'translateY(0)' },
      { opacity: 0, transform: 'translateY(8px)' },
    ],
    options: { duration: 400, easing: 'cubic-bezier(.4,0,.6,1)', fill: 'forwards' },
  },
};

const pulse: MotionPresetSpec = {
  enter: {
    keyframes: [
      { opacity: 0, transform: 'scale(0.9)' },
      { opacity: 1, transform: 'scale(1)' },
    ],
    options: { duration: 400, easing: SPRING_STIFF, fill: 'forwards' },
  },
  exit: {
    keyframes: [{ opacity: 1 }, { opacity: 0 }],
    options: { duration: 350, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
  },
};

const slide: MotionPresetSpec = {
  enter: {
    keyframes: [
      { opacity: 0, transform: 'translateX(-24px)' },
      { opacity: 1, transform: 'translateX(0)' },
    ],
    options: { duration: 250, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' },
  },
  exit: {
    keyframes: [
      { opacity: 1, transform: 'translateX(0)' },
      { opacity: 0, transform: 'translateX(24px)' },
    ],
    options: { duration: 250, easing: 'cubic-bezier(.7,.3,.7,0)', fill: 'forwards' },
  },
};

const bounce: MotionPresetSpec = {
  enter: {
    keyframes: [
      { opacity: 0, transform: 'scale(0.6)' },
      { opacity: 1, transform: 'scale(1.08)', offset: 0.7 },
      { opacity: 1, transform: 'scale(1)' },
    ],
    options: { duration: 500, easing: SPRING_SOFT, fill: 'forwards' },
  },
  exit: {
    keyframes: [
      { opacity: 1, transform: 'scale(1)' },
      { opacity: 0, transform: 'scale(0.95)' },
    ],
    options: { duration: 450, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
  },
};

export const MOTION_SPECS: Record<MotionPreset, MotionPresetSpec> = {
  snap,
  float,
  pulse,
  slide,
  bounce,
};

/**
 * prefers-reduced-motion 감지 시 강제로 snap으로 강등.
 */
export function resolveMotion(preset: MotionPreset, reducedMotion: boolean): MotionPresetSpec {
  if (reducedMotion) return MOTION_SPECS.snap;
  return MOTION_SPECS[preset];
}

/**
 * 요소에 enter 애니메이션을 적용하고 Promise<Animation>을 반환.
 * 호출자가 `.onfinish`나 `await`로 종료를 대기할 수 있습니다.
 */
export function playEnter(el: Element, spec: MotionPresetSpec): Animation {
  return el.animate(spec.enter.keyframes, spec.enter.options);
}

export function playExit(el: Element, spec: MotionPresetSpec): Animation {
  return el.animate(spec.exit.keyframes, spec.exit.options);
}
